export const LAST_ACTIVITY_STORAGE_KEY = 'mailbox.lastActivityAt';
export const SESSION_INACTIVITY_TIMEOUT_MS = 12 * 60 * 60 * 1000;

/** Throttle writes so rapid API/UI events do not hammer localStorage. */
const ACTIVITY_WRITE_THROTTLE_MS = 10 * 1000;
let lastActivityWriteMs = 0;

export function readLastActivityAt(): number | null {
  const raw = localStorage.getItem(LAST_ACTIVITY_STORAGE_KEY);
  if (!raw) return null;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return parsed;
}

export function writeLastActivityAt(timestampMs: number = Date.now()): void {
  localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, String(timestampMs));
  lastActivityWriteMs = timestampMs;
}

/** Page interaction or successful auth API traffic — throttled. */
export function touchLastActivity(timestampMs: number = Date.now()): void {
  if (timestampMs - lastActivityWriteMs < ACTIVITY_WRITE_THROTTLE_MS) return;
  writeLastActivityAt(timestampMs);
}

export function clearLastActivityAt(): void {
  localStorage.removeItem(LAST_ACTIVITY_STORAGE_KEY);
  lastActivityWriteMs = 0;
}

/**
 * Missing / invalid last-activity is treated as inactive so stale tokens
 * without a timestamp cannot rehydrate after many days away.
 */
export function isSessionInactive(
  lastActivityAtMs: number | null,
  nowMs: number = Date.now(),
  timeoutMs: number = SESSION_INACTIVITY_TIMEOUT_MS
): boolean {
  if (!lastActivityAtMs || !Number.isFinite(lastActivityAtMs)) return true;
  return nowMs - lastActivityAtMs >= timeoutMs;
}

/**
 * Clear local auth if tokens exist but the 12h inactivity window has elapsed
 * (or last activity was never recorded). Returns true when auth was cleared.
 */
export function expireStalePersistedSession(nowMs: number = Date.now()): boolean {
  const accessToken = localStorage.getItem('accessToken');
  const userJson = localStorage.getItem('user');
  if (!accessToken && !userJson) {
    return false;
  }

  if (!isSessionInactive(readLastActivityAt(), nowMs)) {
    return false;
  }

  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  clearLastActivityAt();
  return true;
}
